'use client'

import {
  Input,
  Label,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/ui'
import { Info } from 'lucide-react'

import type { AgentTone } from '@/types/agents'

import { PromptCompatibilityCheck } from './prompt-compatibility-check'

interface ProfileTabProps {
  orgId: string | null
  agentId: string | null
  name: string
  assistantName: string
  description: string
  instructions: string
  personality: string
  welcomeMessage: string
  companyName: string
  companyDescription: string
  tone: AgentTone
  onChange: (key: string, value: string) => void
}

function SectionHeader({
  title,
  description,
}: {
  title: string
  description?: string
}) {
  return (
    <div className="mb-4">
      <h3 className="text-foreground text-sm font-semibold">{title}</h3>
      {description && (
        <p className="text-muted-foreground mt-0.5 text-xs">{description}</p>
      )}
    </div>
  )
}

function FormField({
  label,
  required,
  hint,
  children,
}: {
  label: string
  required?: boolean
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1.5">
        <Label className="text-foreground text-sm font-medium">
          {label}
          {required && <span className="text-destructive ml-0.5">*</span>}
        </Label>
        {hint && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="text-muted-foreground size-3.5 cursor-help" />
            </TooltipTrigger>
            <TooltipContent side="right" className="max-w-60 text-xs">
              {hint}
            </TooltipContent>
          </Tooltip>
        )}
      </div>
      {children}
    </div>
  )
}

export function ProfileTab({
  orgId,
  agentId,
  name,
  assistantName,
  description,
  instructions,
  personality,
  welcomeMessage,
  companyName,
  companyDescription,
  onChange,
}: ProfileTabProps) {
  return (
    <div className="flex flex-col gap-4">
      <section className="bg-card rounded-md border p-5">
        <SectionHeader
          title="Identidade"
          description="Informações básicas de identificação e comportamento do agente."
        />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormField
            label="Nome interno"
            required
            hint="Identificador interno, não exibido ao lead"
          >
            <Input
              value={name}
              onChange={(e) => onChange('name', e.target.value)}
              placeholder="Ex: SDR Soja Premium"
            />
          </FormField>

          <FormField
            label="Nome do assistente"
            hint="Nome exibido para o usuário/lead nas conversas"
          >
            <Input
              value={assistantName}
              onChange={(e) => onChange('assistantName', e.target.value)}
              placeholder="Ex: Ana"
            />
          </FormField>

          <div className="md:col-span-2">
            <FormField
              label="Descrição interna"
              hint="Nota de uso interno, não visível ao lead"
            >
              <Textarea
                value={description}
                onChange={(e) => onChange('description', e.target.value)}
                placeholder="Descreva o propósito deste agente para referência interna..."
                rows={3}
                className="resize-none"
              />
            </FormField>
          </div>

          <div className="md:col-span-2">
            <FormField
              label="Instruções principais"
              required
              hint="System prompt — define o comportamento, tom e objetivos do agente"
            >
              <Textarea
                value={instructions}
                onChange={(e) => onChange('instructions', e.target.value)}
                placeholder="Você é... Seu objetivo é... Quando o lead perguntar sobre X, responda..."
                rows={8}
                className="resize-none leading-relaxed"
              />
            </FormField>
            <PromptCompatibilityCheck
              orgId={orgId}
              agentId={agentId}
              instructions={instructions}
              onApply={(value) => onChange('instructions', value)}
            />
          </div>
        </div>
      </section>

      <section className="bg-card rounded-md border p-5">
        <SectionHeader
          title="Personalidade e empresa"
          description="Contexto adicional que enriquece as respostas do agente."
        />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormField
            label="Personalidade"
            hint="Traços de personalidade que guiam o tom das respostas"
          >
            <Input
              value={personality}
              onChange={(e) => onChange('personality', e.target.value)}
              placeholder="Ex: Consultiva, empática, objetiva"
            />
          </FormField>

          <FormField
            label="Nome da empresa"
            hint="Empresa que este agente representa"
          >
            <Input
              value={companyName}
              onChange={(e) => onChange('companyName', e.target.value)}
              placeholder="Ex: AgroSoluções Brasil"
            />
          </FormField>

          <div className="md:col-span-2">
            <FormField
              label="Mensagem de boas-vindas"
              hint="Enviada automaticamente no início de cada nova conversa"
            >
              <Textarea
                value={welcomeMessage}
                onChange={(e) => onChange('welcomeMessage', e.target.value)}
                placeholder="Olá! Sou a Ana da... Como posso te ajudar?"
                rows={3}
                className="resize-none"
              />
            </FormField>
          </div>

          <div className="md:col-span-2">
            <FormField
              label="Descrição da empresa"
              hint="Contexto sobre a empresa injetado no prompt para o agente ter referência"
            >
              <Textarea
                value={companyDescription}
                onChange={(e) => onChange('companyDescription', e.target.value)}
                placeholder="Descreva os principais produtos, mercados atendidos e diferenciais..."
                rows={4}
                className="resize-none"
              />
            </FormField>
          </div>
        </div>
      </section>
    </div>
  )
}
